import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { dateValidator } from '@liberty-financial/ng-core';
import { TagEntityType } from '@features/opportunities/services/tag.service';
import { Tag } from '@shared/models/tag';
import { Task } from '@features/tasks/models/task';
import { TaskService } from '@features/tasks/services/task.service';
import {
  commandEvent,
  CommandEvent
} from '@shared/components/load/command/command.component';
import { StandardSchemaUtil } from '@shared/utilities/standard-schema-util';
import { diff } from 'deep-object-diff';
import { OpportunitySchemaUtil } from '@features/opportunities/modules/core/utils/opportunity-schema-util';

type LoadContent = {
  schema: Task.Definition;
  content: Task.Model;
  tags: Tag[];
};

@Component({ template: '' })
export abstract class TaskDetailsComponent implements OnInit {
  @Input() cancel: () => void = () => {};
  @Input() saved: () => void = () => {};

  abstract get title(): string;
  abstract get saveLabel(): string;
  abstract readonly isEditMode: boolean;
  abstract content(): Observable<Task.Model>;

  taskId!: string;
  entityId!: string;
  tagEntityType: TagEntityType = TagEntityType.Opportunities;
  taskTags: Tag[] | null = null;
  tagsModified = false;
  pendingTags: Tag[] = [];
  load!: LoadContent;

  load$!: Observable<LoadContent>;
  protected change$: Subject<any> = new Subject<any>();
  update$: Observable<CommandEvent<object>> = commandEvent(this.change$, (x) =>
    this.taskService.updateTask(this.entityId, this.taskId, x)
  );
  triggerValidation$ = new Subject<boolean>();

  form = this.fb.group({
    title: [null as string | null, [Validators.maxLength(128)]],
    due: [null as string | null, [Validators.maxLength(10), dateValidator()]],
    priority: [null as string | null],
    notes: [null as string | null, [Validators.maxLength(2000)]]
  });

  constructor(
    protected readonly taskService: TaskService,
    protected fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.taskService.tagEntityType$.subscribe(t => this.tagEntityType = t);

    const loadSchema$ = this.taskService.getTaskDefinition();
    const loadTags$ = this.isEditMode
      ? this.taskService.getTaskTags(this.entityId, this.taskId)
      : of([] as Tag[]);

    this.load$ = forkJoin([loadSchema$, this.content(), loadTags$]).pipe(
      map(([schema, content, tags]) => ({ schema, content, tags })),
      tap((x) => {
        switch (x.schema.type) {
          case 'Client':
            StandardSchemaUtil.attachFormGroupToSchema(this.form, x.schema);
            break;
          case 'Opportunity':
            OpportunitySchemaUtil.attachFormGroupToSchema(this.form, x.schema);
            break;
        }
        this.form.patchValue(x.content);
        this.load = x;
        this.taskTags = x.tags;
      })
    );
  }

  onTagModified(): void {
    this.tagsModified = true;
  }

  onTagsChanged(tags: Tag[]): void {
    this.pendingTags = tags;
  }

  onCancel(): void {
    if (this.tagsModified) {
      this.saved();
    } else {
      this.cancel();
    }
  }

  public save(): void {
    if (this.form.invalid) {
      this.triggerValidation$.next(true);
    } else {
      const formValue = this.form.getRawValue();
      let delta = diff(this.load.content, formValue) as Partial<Task.Model>;

      this.change$.next(delta);
    }
  }
}
